import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CheckCircle2, Send, Trash2, RotateCw, Wallet } from 'lucide-react';
import { formatINR, formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { ThermalPrintButton } from '../../components/ThermalPrintButton.jsx';
import { PdfActions } from '../../components/PdfActions.jsx';
import { TextAreaField, TextField, SelectField } from '../../components/ui/Field.jsx';

const PAYMENT_MODE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'netbanking', label: 'Net banking' },
  { value: 'cheque', label: 'Cheque' },
];

const STATUS_BADGE = {
  draft: 'border-warning text-warning',
  issued: 'border-success text-success',
  cancelled: 'border-danger text-danger',
};

const STATUS_LABEL = { draft: 'Draft', issued: 'Issued', cancelled: 'Cancelled' };

const PAYMENT_STATUS_LABEL = {
  paid: 'Paid in full',
  partial: 'Partially paid',
  unpaid: 'Unpaid',
  // Old gold (or a chit adjustment) taken against the sale was worth more
  // than the sale itself — the shop owes the customer, not the customer.
  refund_due: 'Refund due to customer',
};

const IRN_BADGE = {
  pending: 'border-warning text-warning',
  registered: 'border-success text-success',
  failed: 'border-danger text-danger',
  cancelled: 'border-muted text-muted',
};

const IRN_LABEL = { pending: 'IRN pending', registered: 'IRN registered', failed: 'IRN failed', cancelled: 'IRN cancelled' };

const PAPER_OPTIONS = [
  { value: 'a4', label: 'A4' },
  { value: 'thermal58', label: '58mm thermal' },
  { value: 'thermal80', label: '80mm thermal' },
];


export default function InvoiceViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [paper, setPaper] = useState('a4');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [cancelError, setCancelError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [einvoiceWarning, setEinvoiceWarning] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [payMode, setPayMode] = useState('cash');
  const [payAmount, setPayAmount] = useState('');
  const [payError, setPayError] = useState(null);

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoices', id],
    queryFn: () => api.get(`/invoices/${id}`),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/cancel`, { reason }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setCancelOpen(false);
      setEinvoiceWarning(result?.einvoiceWarning ?? null);
    },
    onError: (err) => setCancelError(err instanceof ApiClientError ? err.message : 'Could not cancel this invoice.'),
  });

  const retryEinvoiceMutation = useMutation({
    mutationFn: () => api.post(`/einvoice/${id}/retry`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoices', id] }),
  });

  const issueMutation = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/issue`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoices'] }),
    onError: (err) => setActionError(err instanceof ApiClientError ? err.message : 'Could not issue this invoice.'),
  });

  const paymentMutation = useMutation({
    mutationFn: (payload) => api.post(`/invoices/${id}/payments`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setPaymentOpen(false);
    },
    onError: (err) => setPayError(err instanceof ApiClientError ? err.message : 'Could not record this payment.'),
  });

  // A negative balance means old gold (or a chit adjustment) taken against
  // this sale was worth more than the sale — the shop owes the customer,
  // rather than the other way round. The direction isn't a choice the
  // counter makes; it follows straight from which way the balance points.
  const isRefundDue = Number(invoice?.balance_amount) < 0;
  const payDirection = isRefundDue ? 'refunded' : 'collected';

  const openPayment = () => {
    setPayError(null);
    setPayMode('cash');
    setPayAmount(invoice.balance_amount != null ? String(Math.abs(Number(invoice.balance_amount))) : '');
    setPaymentOpen(true);
  };

  const submitPayment = (e) => {
    e.preventDefault();
    setPayError(null);
    paymentMutation.mutate({ mode: payMode, direction: payDirection, amount: Number(payAmount) });
  };

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/invoices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate('/invoices');
    },
    onError: (err) => setActionError(err instanceof ApiClientError ? err.message : 'Could not delete this draft.'),
  });

  if (isLoading || !invoice) return null;

  return (
    <div className="flex flex-col gap-5 pb-24">
      <div className="flex items-center gap-3">
        <Link to="/invoices" className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2">
          <ArrowLeft size={20} aria-hidden="true" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{invoice.invoice_no ?? 'Draft invoice'}</h1>
          <p className="text-sm text-muted">{new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        </div>
        <span className="ml-auto flex items-center gap-2">
          {invoice.einvoice ? (
            <span className={`rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wide ${IRN_BADGE[invoice.einvoice.status]}`}>
              {IRN_LABEL[invoice.einvoice.status]}
            </span>
          ) : null}
          <span className={`rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wide ${STATUS_BADGE[invoice.status]}`}>
            {STATUS_LABEL[invoice.status]}
          </span>
        </span>
      </div>

      {invoice.einvoice?.status === 'failed' ? (
        <p className="flex items-center justify-between gap-3 rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          <span>Could not register this invoice with the IRP: {invoice.einvoice.last_error || 'unknown error'}</span>
          <Button
            variant="danger"
            size="sm"
            loading={retryEinvoiceMutation.isPending}
            onClick={() => retryEinvoiceMutation.mutate()}
          >
            <RotateCw size={15} aria-hidden="true" /> Retry
          </Button>
        </p>
      ) : null}

      {einvoiceWarning ? (
        <p className="rounded-card border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">{einvoiceWarning}</p>
      ) : null}

      {invoice.status === 'draft' ? (
        <p className="rounded-card border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          This invoice hasn&rsquo;t been issued yet — it has no invoice number and hasn&rsquo;t affected stock. Issue it
          to finalise, or delete it if it was a mistake.
        </p>
      ) : null}

      <section className="card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Billed to</h2>
          <p className="font-medium">{invoice.customer_name}</p>
          {invoice.customer_mobile ? <p className="text-sm text-muted">{invoice.customer_mobile}</p> : null}
          {invoice.customer_address ? <p className="text-sm text-muted">{invoice.customer_address}</p> : null}
          {invoice.customer_gstin ? <p className="text-sm text-muted">GSTIN: {invoice.customer_gstin}</p> : null}
        </div>
        <div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Payment</h2>
              <p className="font-medium">
                {invoice.payment_status === 'paid' && Number(invoice.refunded_amount) > 0
                  ? 'Refunded in full'
                  : PAYMENT_STATUS_LABEL[invoice.payment_status] ?? 'Unpaid'}
              </p>
              <p className="text-sm text-muted">Paid {formatINR(invoice.paid_amount)}</p>
              {Number(invoice.refunded_amount) > 0 ? (
                <p className="text-sm text-muted">Refunded {formatINR(invoice.refunded_amount)}</p>
              ) : null}
              {Number(invoice.balance_amount) > 0 ? (
                <p className="text-sm text-warning">Balance due {formatINR(invoice.balance_amount)}</p>
              ) : Number(invoice.balance_amount) < 0 ? (
                <p className="text-sm text-info">Refund due to customer {formatINR(Math.abs(invoice.balance_amount))}</p>
              ) : null}
            </div>
            {invoice.status === 'issued' && invoice.payment_status !== 'paid' ? (
              <Button type="button" variant="secondary" size="sm" onClick={openPayment}>
                <Wallet size={15} aria-hidden="true" /> {isRefundDue ? 'Record refund' : 'Record payment'}
              </Button>
            ) : null}
          </div>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Items</h2>
        <div className="table-scroll">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="py-2 pr-3">Item</th>
                <th className="py-2 pr-3 text-right">Net wt.</th>
                <th className="py-2 pr-3 text-right">Rate/g</th>
                <th className="py-2 pr-3 text-right">Making</th>
                <th className="py-2 pr-3 text-right">Taxable</th>
                <th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item) => (
                <tr key={item.id} className="tabular border-b border-border last:border-0">
                  <td className="py-2 pr-3">
                    {item.item_name}
                    {item.purity_label ? <span className="text-muted"> · {item.purity_label}</span> : null}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {formatWeight(item.net_weight)}
                    {Number(item.wastage_percentage) > 0 ? (
                      <div className="text-xs text-muted">+{item.wastage_percentage}% ({formatWeight(item.billed_weight)})</div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3 text-right">{formatINR(item.rate_per_gram)}</td>
                  <td className="py-2 pr-3 text-right">{formatINR(item.making_charges)}</td>
                  <td className="py-2 pr-3 text-right">{formatINR(item.taxable_value)}</td>
                  <td className="py-2 text-right font-medium">{formatINR(item.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {invoice.oldGold?.length ? (
        <section className="card p-5">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Old gold exchanged</h2>
          <div className="table-scroll">
            <table className="w-full min-w-[480px] text-sm">
              <tbody>
                {invoice.oldGold.map((g) => (
                  <tr key={g.id} className="tabular border-b border-border last:border-0">
                    <td className="py-2 pr-3">{g.description}</td>
                    <td className="py-2 pr-3 text-right">{formatWeight(g.net_weight)}</td>
                    <td className="py-2 text-right font-medium">−{formatINR(g.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="card ml-auto w-full max-w-sm p-5">
        <dl className="flex flex-col gap-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-muted">Taxable value</dt><dd className="tabular">{formatINR(invoice.taxable_value)}</dd></div>
          {Number(invoice.cgst_amount) > 0 ? (
            <>
              <div className="flex justify-between"><dt className="text-muted">CGST</dt><dd className="tabular">{formatINR(invoice.cgst_amount)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">SGST</dt><dd className="tabular">{formatINR(invoice.sgst_amount)}</dd></div>
            </>
          ) : null}
          {Number(invoice.igst_amount) > 0 ? (
            <div className="flex justify-between"><dt className="text-muted">IGST</dt><dd className="tabular">{formatINR(invoice.igst_amount)}</dd></div>
          ) : null}
          <div className="flex justify-between"><dt className="text-muted">Round off</dt><dd className="tabular">{formatINR(invoice.round_off)}</dd></div>
          <div className="mt-1 flex justify-between border-t border-border pt-2 text-base font-semibold"><dt>Grand total</dt><dd className="tabular">{formatINR(invoice.grand_total)}</dd></div>
          {Number(invoice.old_gold_value) > 0 ? (
            <div className="flex justify-between"><dt className="text-muted">Old gold</dt><dd className="tabular">−{formatINR(invoice.old_gold_value)}</dd></div>
          ) : null}
          <div className="flex justify-between text-base font-semibold text-primary"><dt>Amount payable</dt><dd className="tabular">{formatINR(invoice.amount_payable)}</dd></div>
        </dl>
        <p className="mt-3 text-xs italic text-muted">{invoice.amount_in_words}</p>
      </section>

      {actionError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {actionError}
        </p>
      ) : null}

      {/* Sticky action bar — reachable with a thumb on a phone. */}
      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        {invoice.status === 'draft' ? (
          <>
            <Button
              onClick={() => { setActionError(null); issueMutation.mutate(); }}
              loading={issueMutation.isPending}
              className="flex-1 sm:flex-none"
            >
              <Send size={17} aria-hidden="true" /> Issue invoice
            </Button>
            <Button
              variant="danger"
              onClick={() => { setActionError(null); setDeleteOpen(true); }}
              className="flex-1 sm:flex-none"
            >
              <Trash2 size={17} aria-hidden="true" /> Delete draft
            </Button>
          </>
        ) : (
          <>
            <select
              value={paper}
              onChange={(e) => setPaper(e.target.value)}
              className="h-touch rounded-card border border-border bg-surface px-3 text-sm"
            >
              {PAPER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {/* Was two buttons — "Print / View" and "PDF" — wired to the same
                handler, identical behaviour under different labels. The second
                slot is the share action now, which is what it was reaching for. */}
            <PdfActions
              path={`/invoices/${id}/pdf`}
              query={{ paper }}
              documentLabel={`Invoice ${invoice.invoice_no}`}
              fileBaseName={invoice.invoice_no}
              onError={setActionError}
            />
            <ThermalPrintButton printPath={`/invoices/${id}`} />
            {invoice.status !== 'cancelled' ? (
              <Button variant="danger" onClick={() => setCancelOpen(true)} className="flex-1 sm:flex-none">
                <Ban size={17} aria-hidden="true" /> Cancel
              </Button>
            ) : null}
          </>
        )}
      </div>

      <Modal open={paymentOpen} onClose={() => setPaymentOpen(false)} title={isRefundDue ? 'Record a refund' : 'Record a payment'}>
        <form onSubmit={submitPayment} className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            {isRefundDue
              ? `Old gold taken on this invoice was worth more than the sale — ${formatINR(Math.abs(invoice.balance_amount))} is due back to the customer. Record what was actually handed to them.`
              : `Balance due is ${formatINR(invoice.balance_amount)}.`}{' '}
            This is added to the invoice&rsquo;s payment history — it doesn&rsquo;t reprint or reissue anything.
          </p>
          <SelectField label="Mode" value={payMode} onChange={(e) => setPayMode(e.target.value)}>
            {PAYMENT_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SelectField>
          <TextField
            label="Amount"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            max={Math.abs(invoice.balance_amount)}
            required
            autoFocus
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
          />
          {payError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {payError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setPaymentOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!(Number(payAmount) > 0)} loading={paymentMutation.isPending}>
              <Wallet size={16} aria-hidden="true" /> {isRefundDue ? 'Record refund' : 'Record payment'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete this draft?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            This draft never took an invoice number and hasn&rsquo;t affected stock, so deleting it leaves no trace and
            no gap in the series. This can&rsquo;t be undone.
          </p>
          {actionError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {actionError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
              Keep draft
            </Button>
            <Button variant="danger" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
              <Trash2 size={16} aria-hidden="true" /> Delete
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel this invoice?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            The invoice number <b>{invoice.invoice_no}</b> stays reserved and is never reused — cancelling records why,
            it doesn&rsquo;t erase the record. Any stock sold on this invoice returns to hand.
          </p>
          <TextAreaField label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          {cancelError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {cancelError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              Keep invoice
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              loading={cancelMutation.isPending}
              onClick={() => {
                setCancelError(null);
                cancelMutation.mutate();
              }}
            >
              <CheckCircle2 size={16} aria-hidden="true" /> Confirm cancellation
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
